import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  ShoppingCart, 
  Trash2, 
  ArrowRight, 
  Truck, 
  ShieldCheck, 
  Tag, 
  ArrowLeft,
  Sparkles,
  Lock,
  Check
} from 'lucide-react';
import { formatINR } from '../data/products';

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    cartCount,
    cartSubtotal,
    discountAmount,
    gstAmount,
    cartTotal,
    shippingFee = 0,
    removeFromCart,
    updateQuantity,
    couponCode,
    couponMessage,
    appliedDiscountPercent,
    applyCoupon,
    removeCoupon,
    masterMetalRates,
    calculateProductPrice
  } = useShop();

  const [inputCoupon, setInputCoupon] = useState('');
  const safeCart = Array.isArray(cart) ? cart : [];

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    if (!inputCoupon.trim()) return;
    applyCoupon(inputCoupon);
    setInputCoupon('');
  };

  const estimatedMRP = Math.round(cartSubtotal * 1.15);
  const totalSavings = (estimatedMRP - cartSubtotal) + discountAmount;

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#f1f3f6', color: '#212121', padding: '16px 0 50px' }}>
      <div className="container">
        
        {/* Flipkart Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#878787', marginBottom: '14px' }}>
          <Link to="/" style={{ color: '#2874f0', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <span style={{ color: '#212121', fontWeight: 600 }}>My Cart ({cartCount})</span>
        </div>

        {safeCart.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 1.5rem',
            backgroundColor: '#ffffff',
            borderRadius: '2px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            maxWidth: '800px',
            margin: '2rem auto'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: '#f1f3f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <ShoppingCart size={54} color="#2874f0" />
            </div>
            <h2 style={{ color: '#212121', fontSize: '1.4rem', fontWeight: 700, marginBottom: '6px' }}>
              Your Cart is empty!
            </h2>
            <p style={{ color: '#878787', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Explore our best jewellery and pure silverware collections to add items to your cart.
            </p>
            <Link 
              to="/" 
              style={{
                backgroundColor: '#2874f0',
                color: '#ffffff',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 32px',
                borderRadius: '2px',
                fontWeight: 600,
                fontSize: '0.9rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              Shop Now
            </Link>
          </div>
        ) : (
          <div 
            className="fk-cart-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '16px',
              alignItems: 'start'
            }}
          >
            
            {/* Left Column: Flipkart My Cart Items Box */}
            <div style={{ flex: '1 1 65%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '2px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}>
                {/* Cart Header */}
                <div style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 700, color: '#212121', fontSize: '1.05rem' }}>
                    My Cart ({safeCart.length})
                  </span>
                  <span style={{ fontSize: '0.82rem', color: '#388e3c', fontWeight: 600 }}>
                    ✓ 100% BIS Hallmarked Purity Assured
                  </span>
                </div>

                {/* Items List */}
                <div>
                  {safeCart.map((item, idx) => {
                    const product = item?.product || item;
                    if (!product) return null;
                    const pricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
                    const unitPrice = pricing?.finalPrice || Number(product.price) || 0;
                    const prodId = product.id || `cart-${idx}`;
                    const lineTotal = unitPrice * (item.quantity || 1);
                    const itemMRP = Math.round(unitPrice * 1.15);
                    const itemDiscount = Math.round(((itemMRP - unitPrice) / itemMRP) * 100);

                    return (
                      <div 
                        key={prodId}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '110px 1fr',
                          gap: '16px',
                          padding: '18px 20px',
                          borderBottom: idx < safeCart.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        {/* Thumbnail & Quantity */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                          <Link 
                            to={`/product/${product.id}`}
                            style={{
                              width: '90px',
                              height: '90px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '2px',
                              overflow: 'hidden',
                              backgroundColor: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img 
                              src={product.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=400&q=80'} 
                              alt={product.name}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          </Link>

                          {/* Flipkart Quantity Controls */}
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <button
                              onClick={() => updateQuantity(product.id, (item.quantity || 1) - 1)}
                              style={{
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                border: '1px solid #c2c2c2',
                                background: '#fff',
                                color: '#212121',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              -
                            </button>
                            <span style={{
                              width: '32px',
                              height: '24px',
                              border: '1px solid #c2c2c2',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#fff'
                            }}>
                              {item.quantity || 1}
                            </span>
                            <button
                              onClick={() => updateQuantity(product.id, (item.quantity || 1) + 1)}
                              style={{
                                width: '26px',
                                height: '26px',
                                borderRadius: '50%',
                                border: '1px solid #c2c2c2',
                                background: '#fff',
                                color: '#212121',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Details */}
                        <div>
                          <Link 
                            to={`/product/${product.id}`}
                            style={{ 
                              color: '#212121', 
                              fontWeight: 500, 
                              fontSize: '0.98rem', 
                              textDecoration: 'none',
                              display: 'block',
                              marginBottom: '6px'
                            }}
                          >
                            {product.name}
                          </Link>

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.78rem', color: '#878787', marginBottom: '8px' }}>
                            <span>Seller: M.L. Jewellers Flagship</span>
                            <span style={{
                              backgroundColor: '#2874f0',
                              color: '#ffffff',
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              padding: '1px 5px',
                              borderRadius: '2px',
                              fontStyle: 'italic'
                            }}>
                              ML-Assured ★
                            </span>
                          </div>

                          {/* Price Row */}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '14px' }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#212121' }}>
                              {formatINR(lineTotal)}
                            </span>
                            <span style={{ fontSize: '0.85rem', color: '#878787', textDecoration: 'line-through' }}>
                              {formatINR(itemMRP * (item.quantity || 1))}
                            </span>
                            <span style={{ fontSize: '0.85rem', color: '#388e3c', fontWeight: 700 }}>
                              {itemDiscount}% off
                            </span>
                          </div>

                          {/* Actions: Save for later & Remove */}
                          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                            <button
                              onClick={() => removeFromCart(product.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#212121',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                textTransform: 'uppercase'
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#2874f0'}
                              onMouseLeave={(e) => e.target.style.color = '#212121'}
                            >
                              REMOVE
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Flipkart Sticky "PRICE DETAILS" Box */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '2px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '12px 18px',
                  borderBottom: '1px solid #f0f0f0',
                  color: '#878787',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  textTransform: 'uppercase'
                }}>
                  PRICE DETAILS
                </div>

                <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#212121' }}>
                    <span>Price ({safeCart.length} items)</span>
                    <span>{formatINR(estimatedMRP)}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#388e3c' }}>
                    <span>Discount</span>
                    <span>-{formatINR(estimatedMRP - cartSubtotal)}</span>
                  </div>

                  {discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#388e3c' }}>
                      <span>Coupon Discount</span>
                      <span>-{formatINR(discountAmount)}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#212121' }}>
                    <span>Delivery Charges</span>
                    <span style={{ color: '#388e3c', fontWeight: 600 }}>FREE</span>
                  </div>

                  {/* Total Amount Dashed Border */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '14px 0',
                    borderTop: '1px dashed #e0e0e0',
                    borderBottom: '1px dashed #e0e0e0',
                    fontSize: '1.15rem',
                    fontWeight: 800,
                    color: '#212121'
                  }}>
                    <span>Total Amount</span>
                    <span>{formatINR(cartTotal)}</span>
                  </div>

                  {/* Green Savings Message */}
                  <div style={{ color: '#388e3c', fontWeight: 700, fontSize: '0.85rem' }}>
                    You will save {formatINR(totalSavings)} on this order
                  </div>

                  {/* Flipkart Bright Orange PLACE ORDER Button */}
                  <Link
                    to="/checkout"
                    style={{
                      backgroundColor: '#fb641b',
                      color: '#ffffff',
                      textDecoration: 'none',
                      padding: '14px',
                      borderRadius: '2px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textAlign: 'center',
                      boxShadow: '0 1px 2px 0 rgba(0,0,0,.2)',
                      textTransform: 'uppercase',
                      marginTop: '6px'
                    }}
                  >
                    PLACE ORDER
                  </Link>
                </div>
              </div>

              {/* Coupon Form */}
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '2px',
                border: '1px solid #e0e0e0',
                padding: '14px 18px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
              }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#212121', marginBottom: '8px' }}>
                  Have a Promo Code or Voucher?
                </div>
                <form onSubmit={handleApplyCoupon} style={{ display: 'flex', gap: '6px' }}>
                  <input 
                    type="text"
                    placeholder="Enter Coupon (e.g. ROYAL10)"
                    value={inputCoupon}
                    onChange={(e) => setInputCoupon(e.target.value.toUpperCase())}
                    style={{
                      flex: 1,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '2px',
                      padding: '8px 10px',
                      color: '#212121',
                      fontSize: '0.84rem',
                      outline: 'none'
                    }}
                  />
                  <button 
                    type="submit" 
                    style={{
                      backgroundColor: '#2874f0',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '2px',
                      fontWeight: 600,
                      fontSize: '0.84rem',
                      cursor: 'pointer'
                    }}
                  >
                    Apply
                  </button>
                </form>
                {couponMessage && (
                  <div style={{ fontSize: '0.78rem', marginTop: '6px', color: couponCode ? '#388e3c' : '#d32f2f' }}>
                    {couponMessage}
                  </div>
                )}
                {couponCode && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '8px',
                    padding: '6px 10px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '2px',
                    fontSize: '0.8rem',
                    color: '#388e3c',
                    fontWeight: 600
                  }}>
                    <span>"{couponCode}" Applied</span>
                    <button type="button" onClick={removeCoupon} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '0.78rem' }}>
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Security Trust Note */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                color: '#878787',
                fontSize: '0.78rem'
              }}>
                <ShieldCheck size={26} color="#878787" style={{ flexShrink: 0 }} />
                <span>Safe and Secure Payments. 100% Authentic products. Direct artisan pricing.</span>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
